import type { Layout } from './layout';
import { computeLayout } from './layout';

export interface CanvasContext {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  getLayout(): Layout;
  resize(): void;
}

export function setupCanvas(
  canvas: HTMLCanvasElement,
  onLayout?: (layout: Layout) => void,
): CanvasContext {
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Failed to acquire 2D context');

  let layout = computeLayout(window.innerWidth, window.innerHeight);

  function resize(): void {
    const dpr = window.devicePixelRatio || 1;
    const w = window.innerWidth;
    const h = window.innerHeight;
    canvas.width = Math.floor(w * dpr);
    canvas.height = Math.floor(h * dpr);
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
    layout = computeLayout(w, h);
    onLayout?.(layout);
  }

  resize();
  window.addEventListener('resize', resize);
  // iOS Safari's URL bar dynamically shows/hides; window.innerHeight lags
  // behind these transitions. visualViewport.resize fires reliably for them,
  // and orientationchange covers landscape/portrait flips that occasionally
  // skip the window resize event on iOS.
  window.visualViewport?.addEventListener('resize', resize);
  window.addEventListener('orientationchange', resize);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') resize();
  });

  return {
    canvas,
    ctx,
    getLayout: () => layout,
    resize,
  };
}
